import React, { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from "papaparse";
import MDConfirmAlert from "components/MDConfirmAlert";

function DwhFilesProccess() {
  const { DwhFileProccessColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [summary, setSummary] = useState([]);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false); // Loading state for API calls

  const api = useApi(); // Using the custom `useApi` hook for API calls

  useEffect(() => {
    fetchSettlementReport(dateRange);
  }, [dateRange]);

  // Function to fetch settlement report based on date range
  const fetchSettlementReport = useCallback(async (dateRange) => {
    setLoading(true);
    try {
      const response = await api.get(
        `reports/dwhFilesProccess/list?startDate=${formatDate(dateRange[0])}&endDate=${formatDate(dateRange[1])}`
      );
      if (response.data.statusCodeValue === 200) {
        const modifiedData = response.data.body.map((item) => ({
          ...item,
          action: renderActions(item),
        }));
        setSummary(modifiedData);
    
      } else {
        const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
        setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      }
    } catch (error) {
      const errorMessage =
        error.response?.status === 403
          ? "You do not have permission to perform this action."
          : "Failed to load records.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Format date to 'yyyy-MM-dd'
// Format date to 'yyyy-MM-dd' in Australia/Sydney timezone
const formatDate = (date) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Australia/Sydney' };
  const formatter = new Intl.DateTimeFormat('en-AU', options);
  const parts = formatter.formatToParts(date);
  const year = parts.find(part => part.type === 'year').value;
  const month = parts.find(part => part.type === 'month').value;
  const day = parts.find(part => part.type === 'day').value;

  return `${year}-${month}-${day}`;
};


  // Renders the action buttons for each row
  const renderActions = (item) => (
    <MDBox display="flex" alignItems="center">
      {/* Pass the fileName instead of fileId */}
      <Icon fontSize="small" style={{ cursor: "pointer" }} onClick={() => handleDownload(item.fileName)}>
        download
      </Icon>
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => showConfirmationDialog(item)}
        ml={2}
        disabled={item.flag} // Disable the button if flag is true (already processed)
      >
        Process
      </MDButton>
    </MDBox>
  );

  // Handle file download using fileName
  const handleDownload = (fileName) => {
    // Construct the download URL using fileName instead of fileId
    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/api/adminPortal/v1/dwh-file/download/${fileName}`;
    window.open(downloadUrl, "_blank");
  };

  // Send the file for processing
  const handleSend = async (item) => {
    setLoading(true);
    try {
      const response = await api.get(`reports/proccessDwhFile/${item.fileName}`);
      if (response.data) {
        toast.success(`File ${item.fileName} moved successfully!`);
        window.location.reload();
      } else {
        toast.error("Failed to move the file.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.status === 404
          ? `File not found: ${item.fileName}`
          : error.response?.status === 403
          ? "You do not have permission to perform this action."
          : `An error occurred: ${error.message}`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  // Show confirmation dialog for processing the file
  const showConfirmationDialog = (item) => {
    setSelectedItem(item);
    setShowConfirmDialog(true);
  };

  // Generate CSV content from the table data
  const generateCSV = () => {
    if (summary.length === 0) return "";

    const headers = DwhFileProccessColumns.filter((column) => column.Header !== "Action").map(
      (column) => column.Header
    );
    const summaryData = summary.map((item) =>
      DwhFileProccessColumns.filter((column) => column.Header !== "Action").map(
        (column) => item[column.accessor]
      )
    );

    return Papa.unparse([headers, ...summaryData], { quotes: true });
  };

  // Download the table data as a CSV file
  const handleDownloadCSV = () => {
    if (summary.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false }); // Prevents auto-closing
      setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      
      return;
    }
    const blob = new Blob([generateCSV()], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `DwhFileProccessColumns${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
    link.click();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          {/* Centering DateRangePicker */}
          <Grid item xs={12} display="flex" justifyContent="center">
            <DateRangePicker
              name="date-range"
              disabledDate={afterToday()}
              ranges={predefinedRanges}
              onChange={(range) => setDateRange(range)}
              format="yyyy-MM-dd"
              value={dateRange}
              placeholder="Select Date Range"
            />
          </Grid>
          {/* Export Button Aligned to the Left */}
          <Grid item xs={12} display="flex" justifyContent="flex-start">
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginLeft: "0px", marginBottom: "12px" }}
            
            >
              Export
            </MDButton>
          </Grid>
          {/* DataTable Section */}
          <Grid item xs={12}>
            <DataTable
              table={{ columns: DwhFileProccessColumns, rows: summary }}
              showTotalEntries
              isSorted
              noEndBorder
              entriesPerPage
              canSearch
              loading={loading} // Pass loading state to DataTable
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {/* Show custom confirmation dialog */}
      {showConfirmDialog && (
        <MDConfirmAlert
          text={`Are you sure you want to process ${selectedItem?.fileName}?`}
          confirmFunction={() => handleSend(selectedItem)} // Call handleSend on confirm
          cancelFunction={() => setShowConfirmDialog(false)}
          toast={toast}
        />
      )}
    </DashboardLayout>
  );
}

export default DwhFilesProccess;
